/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import invert from 'lodash.invert';
import {ReportTabMode} from '../../types/redux';
import {
  ASSET_LOADING_COMPLETED,
  ASSETS_LOADING,
  INSERT_ASSETS,
  RESET_FILTER_TREE_NODES,
  RESET_TREE_NODES,
  SET_FILTER_TREE_NODES,
  SET_GROUP_FILTERS_CHECKED,
  SET_GROUP_FILTERS_EXPANDED,
  SET_GROUPS_NODE_KEY_MAP,
  SET_INCLUDE_SC_DEVICES,
  SET_SC_DEVICES_TYPES,
  UPDATE_GROUP_FILTERS_CHECKED,
  UPDATE_ITEM_STORED
} from '../actions/groups-filter';

export interface GroupFilterState {
  treeNodes: any;
  nodeKeyMap: {[key: string]: string};
  checkedNodesTracking: string[];
  expandedNodesTracking: string[];
  checkedNodesHistory: string[];
  expandedNodesHistory: string[];
  checkedNodesShipments: string[];
  expandedNodesShipments: string[];
  loadingPending: boolean;
  assetKeyMap: CachedData;
  includeSCDevices: boolean;
  SCDeviceTypes: string;
}

export interface CachedData {
  [id: string]: CachedItem
}

interface CachedItem {
  id: number | string;
  title: string;
  key: number | string;
  accountId: number | string;
  isDevice: boolean;
  isAsset: boolean
}

export const initialState: GroupFilterState = {
  treeNodes              : null,
  nodeKeyMap             : null,
  checkedNodesTracking   : [],
  expandedNodesTracking  : ['0'],
  checkedNodesHistory    : [],
  expandedNodesHistory   : ['0'],
  checkedNodesShipments  : [],
  expandedNodesShipments : ['0'],
  loadingPending         : false,
  assetKeyMap            : {},
  includeSCDevices       : false,
  SCDeviceTypes          : null
};

const reducers = {
  [SET_FILTER_TREE_NODES] : (draft: GroupFilterState, {nodes}) => {
    draft.treeNodes = nodes;
  },
  [RESET_FILTER_TREE_NODES] : (draft: GroupFilterState, {mode}) => {
    if (mode === ReportTabMode.ShipmentHistory) {
      draft.expandedNodesShipments = ['0'];
      draft.checkedNodesShipments  = [];
    } else if (mode === ReportTabMode.AssetHistory) {
      draft.expandedNodesHistory = ['0'];
      draft.checkedNodesHistory  = [];
    } else {
      draft.expandedNodesTracking = ['0'];
      draft.checkedNodesTracking  = [];
    }
    draft.loadingPending = false;
  },
  [SET_GROUPS_NODE_KEY_MAP] : (draft: GroupFilterState, {keys}) => {
    draft.nodeKeyMap = keys;
  },
  [SET_GROUP_FILTERS_CHECKED] : (draft: GroupFilterState, {keys, mode}) => {
    if (mode === ReportTabMode.ShipmentHistory) {
      draft.checkedNodesShipments = keys;
    } else if (mode === ReportTabMode.AssetHistory) {
      draft.checkedNodesHistory = keys;
    } else {
      draft.checkedNodesTracking = keys;
    }
  },
  [UPDATE_GROUP_FILTERS_CHECKED] : (draft: GroupFilterState, {item}) => {
    const {id, selected} = item;
    const keyToRemove = [];
    if (id === 'assets' || id === 'devices') {
      keyToRemove.push(draft.assetKeyMap[selected.id] ? draft.assetKeyMap[selected.id].key : undefined);
    }
    if (id === 'groups') {
      const keyMapInverted = invert(draft.nodeKeyMap);
      const groupToRemove = keyMapInverted[selected.id];
      keyToRemove.push(groupToRemove);
      keyToRemove.push(`-${groupToRemove}`);
    }
    const newValues = draft.checkedNodesTracking.filter((node) => !keyToRemove.includes(node));
    draft.checkedNodesTracking = newValues;
  },
  [UPDATE_ITEM_STORED] : ({assetKeyMap}: GroupFilterState, {itemInfo}) => {
    const found = assetKeyMap[itemInfo.id];
    if (!found) {
      assetKeyMap[itemInfo.id] = itemInfo;
    }
  },
  [SET_GROUP_FILTERS_EXPANDED] : (draft: GroupFilterState, {keys, mode}) => {
    if (mode === ReportTabMode.ShipmentHistory) {
      draft.expandedNodesShipments = keys;
    } else if (mode === ReportTabMode.AssetHistory) {
      draft.expandedNodesHistory = keys;
    } else {
      draft.expandedNodesTracking = keys;
    }
  },
  [ASSETS_LOADING] : (draft: GroupFilterState, {treeNodes}) => {
    draft.treeNodes      = treeNodes;
    draft.loadingPending = true;
  },
  [ASSET_LOADING_COMPLETED] : (draft: GroupFilterState, {treeNodes}) => {
    draft.treeNodes      = treeNodes;
    draft.loadingPending = false;
  },
  [INSERT_ASSETS] : (draft: GroupFilterState, {treeNodes, insertedKeys}) => {
    draft.treeNodes      = treeNodes;
    Object.values(insertedKeys).forEach(({id}) => {
      if (!draft.assetKeyMap[id]) {
        draft.assetKeyMap[id] = insertedKeys[id];
      }
    });
    draft.loadingPending = false;
  },
  [RESET_TREE_NODES] : (draft: GroupFilterState, {nodes}) => {
    draft.treeNodes = nodes;
    draft.nodeKeyMap     = null;
    draft.assetKeyMap    = {};
    draft.loadingPending = false;
  },
  [SET_INCLUDE_SC_DEVICES] : (draft: GroupFilterState, {state}) => {
    draft.includeSCDevices = state;
  },
  [SET_SC_DEVICES_TYPES] : (draft: GroupFilterState, {deviceIds}) => {
    draft.SCDeviceTypes = deviceIds;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: GroupFilterState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
